// Client-specific configuration for the Clarion Wire content-provenance demo —
// the same templatization seam as the other demos' scenarios. Components read
// from this object and never hard-code client names or narrative text.
//
// Clarion leads with a distinct LAYOUT — a published news story with a
// C2PA-style content credential (ContentCredential) that expands into a
// verifiable provenance manifest — with the live seal behind a second tab. A
// toggle switches between two recorded receipts: a story a human editor signed
// off, and one the agent auto-published with no sign-off.
//
// Every displayed fact traces to sealed evidence: the article text shown is the
// exact text the content_hash binds; the manifest rows show only sealed values
// (sources, model, claims count, editor reference, sign-off status).

import type { LivePanelCopy } from "@demo/ui/LivePanel.js";
import type { ContentCredentialConfig, ProvStep } from "@demo/ui/ContentCredential.js";
// Relative (not the @demo alias) because this is a runtime VALUE import: the
// node test runner (tests/demo-newsroom-verify.test.ts imports this file)
// resolves the root tsconfig, which has no @demo/* path. Vite resolves the
// relative path to the same shared module. The type-only @demo imports above
// are erased at runtime, so the alias is fine for them.
import {
  CLARION_ARTICLE_HEADLINE,
  CLARION_ARTICLE_LEDE,
  clarionArticleContent
} from "../../../demo/src/live/content-article.js";

export interface ClarionScenario {
  client: { name: string; domain: string; mark: string };
  product: { name: string; tagline: string };
  disclosure: string;
  masthead: string;
  intro: { headline: string; body: string };
  // Overview tab: the content credential. Scenario bundleIds key into
  // NEWSROOM_BUNDLES (apps/newsroom-demo/src/data.ts).
  credential: ContentCredentialConfig;
  liveCta: { label: string; sub: string };
  live: LivePanelCopy;
}

// The published article comes from the single source of truth
// (@demo/live/content-article.ts): the gig hashes clarionArticleContent() into
// content_hash, and the ContentCredential UI recomputes the same serialization
// from `articleContent` (below) and compares it to the sealed hash — so what a
// reader sees is provably what the credential binds.
const ARTICLE_HEADLINE = CLARION_ARTICLE_HEADLINE;
const ARTICLE_LEDE = CLARION_ARTICLE_LEDE;

// The provenance manifest steps shared by both scenarios; the sign-off and
// publish steps differ per scenario (built below).
const BASE_STEPS: ProvStep[] = [
  {
    icon: "sources",
    label: "Sourced",
    detail: "3 sources · Reuters, Associated Press, County Water Board minutes (all allowlisted)"
  },
  {
    icon: "draft",
    label: "Drafted",
    detail: "model claude-sonnet-4.6 · article & prompt sealed by hash"
  },
  {
    icon: "check",
    label: "Fact-checked",
    detail: "9 claims checked against the sourced material"
  }
];

export const CLARION: ClarionScenario = {
  client: {
    name: "Clarion Wire",
    domain: "readclarion.com",
    mark: "C"
  },
  product: {
    name: "Sequesign",
    tagline: "Verifiable receipts for agent actions"
  },
  disclosure:
    "Fictional demonstration. Clarion Wire, the story, and the byline shown are not real. The article text and the model prompt are sealed only as hashes. The receipt carries no raw prompt. Each content credential is a real Sequesign receipt, sealed by an in-process demo witness and verified live in your browser.",
  masthead: "Every story, sourced and signed",
  credential: {
    eyebrow: "See it in 15 seconds",
    headline:
      "An AI wrote and published this story. Who sourced it, and did an editor sign off? Verify the credential.",
    sub: "Clarion's newsroom agent drafts from an allowlist of sources and publishes under an editorial mandate: a human editor must sign off first. Every step (sources, model, fact-check, sign-off, publish) is sealed into a content credential attached to the story. Don't take the byline's word for it; recompute it yourself:",
    credTitle: "Provenance manifest",
    credSub: "mandate content_provenance · story CW-2026-3391",
    // The canonical serialization of the displayed article — the UI recomputes
    // its hash and compares it to the credential's sealed content_hash.
    articleContent: clarionArticleContent(),
    mandate: {
      sourcesLabel: "Allowed sources",
      sources: "Reuters · AP · public records",
      signoffLabel: "Before publish",
      signoff: "human editor sign-off"
    },
    scenarios: [
      {
        key: "signed-off",
        toggleLabel: "Editor signed off",
        bundleId: "story-signed-off",
        article: {
          kicker: "Environment · Water",
          headline: ARTICLE_HEADLINE,
          byline: { lead: "By the Clarion AI Desk", mid: "reviewed by M. Okafor" },
          lede: ARTICLE_LEDE
        },
        steps: [
          ...BASE_STEPS,
          {
            icon: "signoff",
            label: "Editor sign-off",
            detail: "M. Okafor, managing editor · sign-off sealed in the credential"
          },
          {
            icon: "publish",
            label: "Published",
            detail: "signoff_status: signed_off · article sealed by hash · readclarion.com"
          }
        ],
        summaryTitle: "Content credential: sealed, ready to verify",
        summaryDetail:
          "Five steps, bound into one witnessed credential. Verify to confirm every signature, the content hash, and that the story was sourced from the allowlist and signed off by a human editor before publishing.",
        expectConformant: true
      },
      {
        key: "auto",
        toggleLabel: "Auto-published (no sign-off)",
        bundleId: "story-auto",
        article: {
          kicker: "Environment · Water",
          headline: ARTICLE_HEADLINE,
          byline: {
            lead: "By the Clarion AI Desk",
            mid: "auto-published, no editor review",
            midWarn: true
          },
          lede: ARTICLE_LEDE
        },
        steps: [
          ...BASE_STEPS,
          {
            icon: "signoff",
            label: "Editor sign-off",
            detail: "no human editor signed off before publish",
            flag: "missing"
          },
          {
            icon: "publish",
            label: "Published",
            detail: "signoff_status: auto · article sealed by hash · readclarion.com"
          }
        ],
        summaryTitle: "Content credential: sealed, ready to verify",
        summaryDetail:
          "Five steps, bound into one witnessed credential. The manifest already shows no editor sign-off. Verify to confirm the credential is authentic, and that the mandate flags the missing human sign-off.",
        expectConformant: false
      }
    ],
    verifyLabel: "Verify the credential",
    verifyingLabel: "Verifying…",
    reverifyLabel: "Verify again",
    browserNote:
      "The whole credential is one receipt. Verifying it checks every step together. Switch to the auto-published story to see the same credential when the agent skips the editor: still authentic, honestly recording conformant: false. The credential catches the missing human in the loop; it doesn't hide it."
  },
  liveCta: {
    label: "Run a real seal with your own keys",
    sub: "Presenter mode: seal a brand-new content credential against the real witness, end to end, and verify it here."
  },
  intro: {
    headline: "Every published story, on the record.",
    body: "Clarion's newsroom agent drafts from an allowlist of sources and publishes under an editorial mandate: a human editor must sign off first. Enter a story and run it: the agent seals a real Sequesign content credential server-side (sourced → drafted → fact-checked → signed off → published), then you verify it right here in your browser. Auto-publish without the editor, or add an off-allowlist source, and the sealed credential honestly records conformant: false. It still verifies as authentic; the breach is recorded, not hidden. The article text and the model prompt are only hashed, never sealed raw."
  },
  live: {
    title: "Live run: real seal, real witness",
    intro: [
      "Enter a story and run it: the agent executes a real Sequesign session with your own agent key, seals the provenance chain step by step, and gets each action countersigned by a real witness, then verifies it here in your browser.",
      "The result is verified in EXTERNAL trust-anchor mode: the witness's published keys are fetched from its well-known endpoint (directly, or relayed unchanged through this site) and used as the trust anchor, so this is an independent third-party check, not an integrity self-check.",
      "The editorial mandate binds an allowlist of sources and requires a human editor sign-off before publish. Choose the “auto” publish path (no sign-off), or add the off-allowlist “Anonymous tip” source, and the sealed credential honestly records conformant: false. It still verifies as authentic; the breach is recorded, not hidden. The article text and the model prompt are hashed into the receipt, never sealed raw."
    ],
    contactNote:
      "Live mode contacts the real witness using the keys you enter below. Nothing else on this page makes a network call to that service.",
    keyNote:
      "Keys are used only for this run, in your browser and the request; nothing is stored. They live in React state only, are never written to localStorage, and are cleared when you leave the page.",
    runLabel: "Seal the content credential",
    runningLabel: "Sealing…",
    fields: {
      agentPem: "Agent private key (Ed25519 PEM)",
      witnessBaseUrl: "Witness base URL",
      witnessApiKey: "Witness API key (optional)",
      llmApiKey: "Anthropic API key"
    }
  }
};
